import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { ArrowUpCircle, ArrowDownCircle, XCircle, RefreshCw, CheckCircle2, AlertTriangle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../utils/api';
import { useUsage } from '../hooks/useUsage';

const PLAN_DISPLAY = { free: 'Starter', growth: 'Professional', pro: 'Business', enterprise: 'Enterprise' };

// Phase 11 — Razorpay subscription self-service: Upgrade / Downgrade / Cancel / Reactivate
export const SubscriptionControls = () => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { action, target?, label }
  const { usage, refresh } = useUsage();

  const fetchSub = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/subscriptions/me');
      setInfo(res.data);
    } catch (e) {
      // Razorpay not configured — silently hide controls
      setInfo({ has_subscription: false, subscription: null, cancel_at_cycle_end: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSub(); }, []);

  // Don't render anything for Enterprise (custom-billed) or while loading
  if (loading || usage?.plan === 'enterprise') return null;

  const currentPlan = usage?.plan || 'free';
  const hasSub = !!info?.has_subscription;
  const willCancel = !!info?.cancel_at_cycle_end;

  const runAction = async (action, payload = {}) => {
    setBusy(true);
    try {
      await apiClient.post(`/subscriptions/${action}`, payload);
      toast.success({
        upgrade: 'Plan upgraded — pro-rated charge applied.',
        downgrade: 'Downgrade scheduled for end of current period.',
        cancel: 'Subscription cancelled. Access continues until period end.',
        reactivate: 'Subscription reactivated.',
      }[action] || 'Action completed.');
      await fetchSub();
      await refresh();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Action failed. Please try again.');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  // What buttons to show?
  const canUpgradeToPro      = currentPlan === 'growth' && hasSub;
  const canDowngradeToPro    = currentPlan === 'pro'    && hasSub && !willCancel;
  const canDowngradeToFree   = (currentPlan === 'growth' || currentPlan === 'pro') && hasSub && !willCancel;
  const canCancel            = hasSub && !willCancel && currentPlan !== 'free';
  const canReactivate        = hasSub && willCancel;

  // Nothing to do? Don't render the card.
  if (!hasSub) return null;

  return (
    <>
      <Card data-testid="subscription-controls-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Manage subscription
            {willCancel && (
              <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">
                Ends at period end
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Upgrade, downgrade, cancel or reactivate your {PLAN_DISPLAY[currentPlan]} subscription. No refunds — paid access continues until your current period ends.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {canUpgradeToPro && (
            <Button
              onClick={() => setConfirm({ action: 'upgrade', target: 'pro', label: 'Upgrade to Business' })}
              disabled={busy}
              data-testid="sub-upgrade-btn"
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
            >
              <ArrowUpCircle className="w-4 h-4 mr-1.5" /> Upgrade to Business
            </Button>
          )}

          {canDowngradeToPro && (
            <Button
              onClick={() => setConfirm({ action: 'downgrade', target: 'growth', label: 'Downgrade to Professional' })}
              disabled={busy}
              variant="outline"
              data-testid="sub-downgrade-pro-btn"
            >
              <ArrowDownCircle className="w-4 h-4 mr-1.5" /> Downgrade to Professional
            </Button>
          )}

          {canDowngradeToFree && (
            <Button
              onClick={() => setConfirm({ action: 'downgrade', target: 'free', label: 'Downgrade to Starter (Free)' })}
              disabled={busy}
              variant="outline"
              data-testid="sub-downgrade-free-btn"
            >
              <ArrowDownCircle className="w-4 h-4 mr-1.5" /> Downgrade to Free
            </Button>
          )}

          {canCancel && (
            <Button
              onClick={() => setConfirm({ action: 'cancel', label: 'Cancel subscription' })}
              disabled={busy}
              variant="ghost"
              className="text-rose-600 hover:bg-rose-50"
              data-testid="sub-cancel-btn"
            >
              <XCircle className="w-4 h-4 mr-1.5" /> Cancel
            </Button>
          )}

          {canReactivate && (
            <Button
              onClick={() => setConfirm({ action: 'reactivate', label: 'Reactivate subscription' })}
              disabled={busy}
              data-testid="sub-reactivate-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <RefreshCw className="w-4 h-4 mr-1.5" /> Reactivate
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Confirm dialog */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="subscription-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirm?.action === 'cancel' || confirm?.action === 'downgrade'
                ? <AlertTriangle className="w-5 h-5 text-amber-600" />
                : <Sparkles className="w-5 h-5 text-indigo-600" />}
              {confirm?.label}?
            </DialogTitle>
            <DialogDescription>
              {confirm?.action === 'upgrade' && 'You will be charged a pro-rated amount immediately for the new plan. New limits apply right away.'}
              {confirm?.action === 'downgrade' && 'Downgrade takes effect at the end of your current billing period. You keep your current plan benefits until then. No refunds. Downgrade may be blocked if your current usage exceeds the target plan limits.'}
              {confirm?.action === 'cancel' && 'Your access continues until the end of your current billing period, then your account moves to Starter (Free) automatically. No refunds.'}
              {confirm?.action === 'reactivate' && 'This will undo the cancellation. Your subscription will keep auto-renewing as before.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
            <Button
              onClick={() => {
                const payload = confirm?.target ? { target_plan_type: confirm.target, target_interval: 'monthly' } : {};
                runAction(confirm.action, payload);
              }}
              disabled={busy}
              data-testid="sub-confirm-submit"
              className={confirm?.action === 'cancel' ? 'bg-rose-600 hover:bg-rose-700 text-white' : ''}
            >
              {busy ? 'Working…' : (confirm?.action === 'cancel' ? 'Yes, cancel' : 'Confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
